import { useCallback, useContext } from 'react'
import { toast } from 'react-toastify'
import AuthContext from '../contexts/AuthContext'
import { getMe, microsoftLogin } from '../services/opw.api'
import { IApiError } from '../types/apiError'

/**
 * Trades a Microsoft access token for an app session.
 *
 * Shared because two paths need it: the popup sign-in, which has the token in hand,
 * and the redirect sign-in, which comes back to /redirect and has to acquire one.
 * Only the popup path used to do this, so a user whose popup was blocked signed in
 * with Microsoft and was still logged out of the portal.
 */
export const useMicrosoftSession = () => {
  const authContext = useContext(AuthContext)

  const completeMicrosoftLogin = useCallback(
    async (microsoftToken: string): Promise<boolean> => {
      try {
        const res = await microsoftLogin(microsoftToken)
        if (!res?.access_token) return false

        const userData = await getMe(res.access_token)
        if (!userData?.data?.user) return false

        authContext.updateData('user', userData.data.user)
        authContext.updateData('token', res.access_token)
        toast.success('You successfully logged in', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        })
        return true
      } catch (err) {
        console.error(
          'Backend Microsoft Login Error:',
          (err as IApiError)?.response?.data?.message || (err as IApiError)?.message,
        )
        return false
      }
    },
    [authContext],
  )

  return { completeMicrosoftLogin }
}
